import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { FiPlus, FiTrash2, FiX } from 'react-icons/fi';
import Button from '../../../../../../components/ui/Button';
import InputField from '../../../../../../components/ui/InputField';
import Autocomplete from '../../../../../../components/ui/Autocomplete';
import Toggle from '../../../../../../components/ui/Toggle';
import { BranchActionConfig, ItemVariant, MenuItem, getBranchMenuItems } from '../../../../../../utils/supabaseClient';
import { useBranch } from '../../../../../../context/BranchContext';

interface SelectionOption {
    id: string;
    name: string;
    note: string;
    price: number;
    is_active: boolean;
    is_default: boolean;
    description: string;
    ar: {
        name: string;
        note: string;
        description: string;
    }
}

interface ComboItem {
    id: string;
    item_name: string;
    item_id: string;
    quantity: number;
    note: string;
    useExisting: boolean;
}

interface SelectionOptionPayload {
    name: string;
    price: number;
    is_active: boolean;
    is_default: boolean;
    note?: string;
    description?: string;
    ar?: {
        name?: string;
        note?: string;
        description?: string;
    }
}

interface ComboOverridePayload {
    combo_price: number;
    items: Array<{
        item_name: string;
        quantity: number;
        item_id?: string;
        note?: string;
    }>;
}

interface ItemVariantOverrideModalProps {
    isOpen: boolean;
    onClose: () => void;
    config: BranchActionConfig | null;
    variant: ItemVariant | null;
    onSave: (overrideData: any | null) => Promise<void>;
    isSaving: boolean;
    externalError?: string | null;
}

const generateId = (prefix: string) => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return `${prefix}-${crypto.randomUUID()}`;
    }
    return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const emptySelectionOption = (): SelectionOption => ({
    id: generateId('option'),
    name: '',
    note: '',
    price: 0,
    is_active: true,
    is_default: false,
    description: '',
    ar: {
        name: '',
        note: '',
        description: '',
    },
});

const emptyComboItem = (): ComboItem => ({
    id: generateId('combo-item'),
    item_name: '',
    item_id: '',
    quantity: 1,
    note: '',
    useExisting: true,
});

const ItemVariantOverrideModal: React.FC<ItemVariantOverrideModalProps> = ({
    isOpen,
    onClose,
    config,
    variant,
    onSave,
    isSaving,
    externalError,
}) => {
    const { t } = useTranslation();
    const { branchId } = useBranch();
    const [options, setOptions] = useState<SelectionOption[]>([]);
    const [comboPrice, setComboPrice] = useState<number>(0);
    const [comboItems, setComboItems] = useState<ComboItem[]>([]);
    const [availableMenuItems, setAvailableMenuItems] = useState<MenuItem[]>([]);
    const [error, setError] = useState<string | null>(null);

    const actionType = useMemo(() => config?.action_type.toLowerCase() || '', [config?.action_type]);

    useEffect(() => {
        if (!isOpen || !config || !variant) {
            return;
        }

        setError(null);

        if (actionType === 'selection') {
            // Primary data source: branch_actions_config.data (contains default options)
            const baseOptions = Array.isArray(config.data) ? config.data : [];

            // Override data source: items_variants.override_data (when item needs custom options)
            const overrideOptions = Array.isArray(variant.override_data) ? variant.override_data : null;

            // Modal initialization priority: override_data > base config.data
            const source = overrideOptions !== null ? overrideOptions : baseOptions;

            const normalized: SelectionOption[] = (source && source.length ? source : [emptySelectionOption()]).map((option: any, index: number) => ({
                id: option?.id || generateId(`option-${index}`),
                name: option?.name || '',
                note: option?.note || '',
                price: typeof option?.price === 'number' ? option.price : 0,
                is_active: option?.is_active !== undefined ? option.is_active : true,
                is_default: option?.is_default || false,
                description: option?.description || '',
                ar: {
                    name: option?.ar?.name || '',
                    note: option?.ar?.note || '',
                    description: option?.ar?.description || '',
                },
            }));

            const ensureDefault: SelectionOption[] = normalized.some((opt: SelectionOption) => opt.is_default)
                ? normalized
                : normalized.map((opt: SelectionOption, index: number) => ({ ...opt, is_default: index === 0 }));

            setOptions(ensureDefault);
        } else if (actionType === 'upgrade') {
            // Primary data source: branch_actions_config.data (contains default combo)
            const baseCombo = config.data && !Array.isArray(config.data) ? config.data : null;

            // Override data source: items_variants.override_data (when item needs custom combo)
            const overrideCombo = variant.override_data && !Array.isArray(variant.override_data) ? variant.override_data : null;

            // Modal initialization priority: override_data > base config.data
            const source = overrideCombo || baseCombo || { combo_price: 0, items: [] };

            setComboPrice(typeof source.combo_price === 'number' ? source.combo_price : 0);

            const normalizedItems = Array.isArray(source.items) && source.items.length
                ? source.items
                : [emptyComboItem()];

            setComboItems(
                normalizedItems.map((item: any, index: number) => ({
                    id: item?.id || generateId(`combo-item-${index}`),
                    item_name: item?.item_name || '',
                    item_id: item?.item_id || '',
                    quantity: typeof item?.quantity === 'number' ? item.quantity : 1,
                    note: item?.note || '',
                    useExisting: !!item?.item_id,
                }))
            );
        }
    }, [isOpen, config, variant, variant?.override_data, actionType]);

    // Load available menu items for autocomplete
    useEffect(() => {
        const loadMenuItems = async () => {
            if (!branchId || !isOpen) {
                return;
            }

            try {
                const result = await getBranchMenuItems(branchId);
                if (result.data) {
                    setAvailableMenuItems(result.data);
                }
            } catch (error) {
                console.error('Error loading menu items:', error);
            }
        };

        loadMenuItems();
    }, [branchId, isOpen]);

    // Hydrate combo item names once menu items are available
    useEffect(() => {
        if (!isOpen || actionType !== 'upgrade' || !availableMenuItems.length) {
            return;
        }

        setComboItems((prev) => prev.map((item) => {
            const trimmedId = item.item_id.trim();
            if (item.useExisting && trimmedId && !item.item_name) {
                const match = availableMenuItems.find((menuItem) => menuItem.id === trimmedId);
                if (match) {
                    return { ...item, item_name: match.name };
                }
            }
            return item;
        }));
    }, [availableMenuItems, actionType, isOpen]);

    if (!isOpen || !config || !variant) {
        return null;
    }

    const handleSelectionChange = <K extends keyof SelectionOption>(id: string, field: K, value: SelectionOption[K]) => {
        setOptions((prev) => prev.map((option) => (option.id === id ? { ...option, [field]: value } : option)));
    };

    const addSelectionOption = () => {
        setOptions((prev) => [emptySelectionOption(), ...prev]);
    };

    const removeSelectionOption = (id: string) => {
        setOptions((prev) => {
            if (prev.length <= 1) {
                return prev;
            }

            const filtered = prev.filter((option) => option.id !== id);
            if (!filtered.some((option) => option.is_default) && filtered.length > 0) {
                filtered[0] = { ...filtered[0], is_default: true };
            }

            return filtered;
        });
    };

    const setSelectionDefault = (id: string) => {
        setOptions((prev) => prev.map((option) => ({ ...option, is_default: option.id === id })));
    };

    const handleComboItemChange = <K extends keyof ComboItem>(id: string, field: K, value: ComboItem[K]) => {
        setComboItems((prev) => prev.map((item) => (item.id === id ? { ...item, [field]: value } : item)));
    };

    const handleMenuItemSelection = (comboItemId: string, selectedMenuItemId: string) => {
        const trimmedId = (selectedMenuItemId || '').trim();
        if (!trimmedId) {
            setComboItems((prev) => prev.map((item) =>
                item.id === comboItemId
                    ? { ...item, item_id: '', item_name: '', useExisting: false }
                    : item
            ));
            return;
        }
        const selectedMenuItem = availableMenuItems.find(item => item.id === trimmedId);
        if (selectedMenuItem) {
            setComboItems((prev) => prev.map((item) =>
                item.id === comboItemId
                    ? {
                        ...item,
                        item_id: selectedMenuItem.id,
                        item_name: selectedMenuItem.name,
                        useExisting: true,
                    }
                    : item
            ));
        } else {
            // If no matching item, save the typed value as a custom name
            setComboItems((prev) => prev.map((item) =>
                item.id === comboItemId
                    ? { ...item, item_id: '', item_name: selectedMenuItemId, useExisting: false }
                    : item
            ));
        }
    };

    const addComboItem = () => {
        setComboItems((prev) => [emptyComboItem(), ...prev]);
    };

    const removeComboItem = (id: string) => {
        setComboItems((prev) => (prev.length > 1 ? prev.filter((item) => item.id !== id) : prev));
    };

    const handleSave = async () => {
        setError(null);

        if (actionType === 'selection') {
            const sanitized = options.reduce<SelectionOptionPayload[]>((acc, option) => {
                const name = option.name.trim();
                if (!name) {
                    return acc;
                }

                const payload: SelectionOptionPayload = {
                    name,
                    price: Number.isFinite(option.price) ? option.price : 0,
                    is_active: option.is_active,
                    is_default: option.is_default,
                };

                const note = option.note.trim();
                if (note) {
                    payload.note = note;
                }

                const description = option.description.trim();
                if (description) {
                    payload.description = description;
                }

                // Add Arabic translations if provided
                const arName = option.ar?.name?.trim() || '';
                const arNote = option.ar?.note?.trim() || '';
                const arDescription = option.ar?.description?.trim() || '';

                if (arName || arNote || arDescription) {
                    payload.ar = {};
                    if (arName) payload.ar.name = arName;
                    if (arNote) payload.ar.note = arNote;
                    if (arDescription) payload.ar.description = arDescription;
                }

                acc.push(payload);
                return acc;
            }, []);

            if (!sanitized.length) {
                setError(t('common.variants.validation.optionNameRequired', 'Provide at least one option name.'));
                return;
            }

            await onSave(sanitized);
            return;
        }

        if (actionType === 'upgrade') {
            let missingExistingSelection = false;
            const sanitizedItems = comboItems.reduce<ComboOverridePayload['items']>((acc, item) => {
                const trimmedId = item.item_id.trim();
                const fallbackMenuItem = trimmedId
                    ? availableMenuItems.find(menuItem => menuItem.id === trimmedId)
                    : null;
                const resolvedName = item.item_name.trim() || fallbackMenuItem?.name?.trim() || '';
                const quantity = Number.isFinite(item.quantity) ? item.quantity : 1;

                if (item.useExisting && !trimmedId) {
                    missingExistingSelection = true;
                    return acc;
                }

                if (!resolvedName || quantity <= 0) {
                    return acc;
                }

                const payload: ComboOverridePayload['items'][number] = {
                    item_name: resolvedName,
                    quantity,
                };

                if (trimmedId) {
                    payload.item_id = trimmedId;
                }

                const note = item.note.trim();
                if (note) {
                    payload.note = note;
                }

                acc.push(payload);
                return acc;
            }, []);

            if (missingExistingSelection) {
                setError(t('common.variants.validation.existingItemRequired', 'Select a menu item or switch to custom name.'));
                return;
            }

            if (!sanitizedItems.length) {
                setError(t('common.variants.validation.comboItemsRequired', 'Add at least one combo item with a quantity.'));
                return;
            }

            const comboPayload: ComboOverridePayload = {
                combo_price: Number.isFinite(comboPrice) ? comboPrice : 0,
                items: sanitizedItems,
            };

            await onSave(comboPayload);
        }
    };

    const handleReset = async () => {
        setError(null);
        await onSave(null);
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                    <motion.div
                        className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl bg-white p-6 shadow-xl dark:bg-gray-800"
                        initial={{ scale: 0.95, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.95, opacity: 0 }}
                    >
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                    <span className="inline-block w-3 h-3 bg-amber-500 rounded-full animate-pulse"></span>
                                    {t('common.variants.overrideTitle', 'Customize Variant for Item')}
                                </h2>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                    {t('common.variants.overrideSubtitle', 'These customizations will override the branch defaults and apply only to this menu item.')}
                                </p>

                            </div>
                            <button
                                onClick={onClose}
                                className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
                                type="button"
                            >
                                <FiX className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="mt-6 space-y-6">
                            {actionType === 'selection' && (
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                            {t('common.variants.overrideOptions', 'Customize Options')}
                                        </h3>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            leftIcon={<FiPlus className="h-4 w-4" />}
                                            onClick={addSelectionOption}
                                            type="button"
                                            disabled={isSaving}
                                        >
                                            {t('common.variants.addOption', 'Add Option')}
                                        </Button>
                                    </div>

                                    {/* Options displayed as cards */}
                                    <div className="grid gap-4 sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-2">
                                        {options.map((option) => (
                                            <div key={option.id} className="relative bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-200">
                                                {/* Remove button */}
                                                {options.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeSelectionOption(option.id)}
                                                        disabled={isSaving}
                                                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors disabled:opacity-50"
                                                    >
                                                        <FiTrash2 className="h-3 w-3" />
                                                    </button>
                                                )}

                                                <div className="space-y-3">
                                                    {/* Option Name */}
                                                    <InputField
                                                        label={t('common.variants.optionName', 'Option Name')}
                                                        value={option.name}
                                                        onChange={(event: ChangeEvent<HTMLInputElement>) => handleSelectionChange(option.id, 'name', event.target.value)}
                                                        placeholder="e.g., Small, Medium, Large"
                                                        disabled={isSaving}
                                                    />

                                                    {/* Arabic Option Name */}
                                                    <InputField
                                                        label={t('common.variants.optionNameAr', 'Arabic Option Name (Optional)')}
                                                        value={option.ar?.name || ''}
                                                        onChange={(event: ChangeEvent<HTMLInputElement>) => handleSelectionChange(option.id, 'ar', { ...option.ar, name: event.target.value })}
                                                        placeholder="e.g., صغير, وسط, كبير"
                                                        disabled={isSaving}
                                                    />

                                                    {/* Price */}
                                                    <InputField
                                                        label={t('common.variants.price', 'Price')}
                                                        type="number"
                                                        step="0.01"
                                                        value={option.price}
                                                        onChange={(event: ChangeEvent<HTMLInputElement>) => handleSelectionChange(option.id, 'price', parseFloat(event.target.value) || 0)}
                                                        disabled={isSaving}
                                                    />

                                                    {/* Note */}
                                                    <InputField
                                                        label={t('common.variants.note', 'Note')}
                                                        value={option.note}
                                                        onChange={(event: ChangeEvent<HTMLInputElement>) => handleSelectionChange(option.id, 'note', event.target.value)}
                                                        placeholder="Optional note"
                                                        disabled={isSaving}
                                                    />

                                                    {/* Arabic Note */}
                                                    <InputField
                                                        label={t('common.variants.noteAr', 'Arabic Note (Optional)')}
                                                        value={option.ar?.note || ''}
                                                        onChange={(event: ChangeEvent<HTMLInputElement>) => handleSelectionChange(option.id, 'ar', { ...option.ar, note: event.target.value })}
                                                        placeholder="ملاحظة اختيارية"
                                                        disabled={isSaving}
                                                    />

                                                    {/* Description */}
                                                    <InputField
                                                        label={t('common.variants.description', 'Description')}
                                                        value={option.description}
                                                        onChange={(event: ChangeEvent<HTMLInputElement>) => handleSelectionChange(option.id, 'description', event.target.value)}
                                                        placeholder="Optional description"
                                                        disabled={isSaving}
                                                    />

                                                    {/* Arabic Description */}
                                                    <InputField
                                                        label={t('common.variants.descriptionAr', 'Arabic Description (Optional)')}
                                                        value={option.ar?.description || ''}
                                                        onChange={(event: ChangeEvent<HTMLInputElement>) => handleSelectionChange(option.id, 'ar', { ...option.ar, description: event.target.value })}
                                                        placeholder="وصف اختياري"
                                                        disabled={isSaving}
                                                    />

                                                    {/* Toggles */}
                                                    <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-600">
                                                        <div className="flex items-center gap-2">
                                                            <Toggle
                                                                enabled={option.is_active}
                                                                onChange={(enabled: boolean) => handleSelectionChange(option.id, 'is_active', enabled)}
                                                                disabled={isSaving}
                                                            />
                                                            <span className="text-sm text-gray-600 dark:text-gray-300">
                                                                {t('common.variants.active', 'Active')}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <Toggle
                                                                enabled={option.is_default}
                                                                onChange={(enabled: boolean) => enabled && setSelectionDefault(option.id)}
                                                                disabled={isSaving}
                                                            />
                                                            <span className="text-sm text-gray-600 dark:text-gray-300">
                                                                {t('common.variants.default', 'Default')}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {actionType === 'upgrade' && (
                                <div className="space-y-6">
                                    {/* Combo Price */}
                                    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-700">
                                        <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-200 mb-3">
                                            {t('common.variants.comboConfiguration', 'Combo Configuration')}
                                        </h3>
                                        <InputField
                                            label={t('common.variants.comboPrice', 'Combo Price')}
                                            type="number"
                                            step="0.01"
                                            value={comboPrice}
                                            onChange={(event: ChangeEvent<HTMLInputElement>) => setComboPrice(parseFloat(event.target.value) || 0)}
                                            disabled={isSaving}
                                        />
                                    </div>

                                    {/* Combo Items */}
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                                {t('common.variants.comboItems', 'Combo Items')}
                                            </h3>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                leftIcon={<FiPlus className="h-4 w-4" />}
                                                onClick={addComboItem}
                                                type="button"
                                                disabled={isSaving}
                                            >
                                                {t('common.variants.addItem', 'Add Item')}
                                            </Button>
                                        </div>

                                        {/* Combo Items displayed as cards */}
                                        <div className="grid gap-4 sm:grid-cols-1 lg:grid-cols-2">
                                            {comboItems.map((item) => (
                                                <div key={item.id} className="relative bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-200">
                                                    {/* Remove button */}
                                                    {comboItems.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => removeComboItem(item.id)}
                                                            disabled={isSaving}
                                                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors disabled:opacity-50"
                                                        >
                                                            <FiTrash2 className="h-3 w-3" />
                                                        </button>
                                                    )}

                                                    <div className="space-y-3">
                                                        {/* Menu Item Selection */}
                                                        <div className="flex items-center justify-between">
                                                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                                {t('common.variants.useExistingItem', 'Use existing menu item')}
                                                            </label>
                                                            <Toggle
                                                                enabled={item.useExisting}
                                                                onChange={(enabled: boolean) => {
                                                                    setComboItems((prev) => prev.map((comboItem) =>
                                                                        comboItem.id === item.id
                                                                            ? {
                                                                                ...comboItem,
                                                                                useExisting: enabled,
                                                                                item_id: enabled ? comboItem.item_id : '',
                                                                                item_name: enabled
                                                                                    ? (comboItem.item_id ? comboItem.item_name : '')
                                                                                    : comboItem.item_name,
                                                                            }
                                                                            : comboItem
                                                                    ));
                                                                }}
                                                                disabled={isSaving}
                                                            />
                                                        </div>

                                                        {item.useExisting ? (
                                                            <Autocomplete
                                                                label={t('common.variants.selectMenuItem', 'Select Menu Item')}
                                                                value={item.item_id || ''}
                                                                onChange={(selectedValue: string) => {
                                                                    handleMenuItemSelection(item.id, selectedValue);
                                                                }}
                                                                options={availableMenuItems.map(menuItem => ({
                                                                    value: menuItem.id,
                                                                    label: menuItem.name,
                                                                    description: menuItem.description || undefined
                                                                }))}
                                                                placeholder={t('common.variants.searchMenuItems', 'Search menu items...')}
                                                                allowCustom={true}
                                                                disabled={isSaving}
                                                            />
                                                        ) : (
                                                            <InputField
                                                                label={t('common.variants.customItemName', 'Custom Item Name')}
                                                                value={item.item_name}
                                                                onChange={(event: ChangeEvent<HTMLInputElement>) => setComboItems((prev) => prev.map((comboItem) =>
                                                                    comboItem.id === item.id
                                                                        ? {
                                                                            ...comboItem,
                                                                            item_name: event.target.value,
                                                                        }
                                                                        : comboItem
                                                                ))}
                                                                placeholder={t('common.variants.enterCustomItemName', 'Enter custom item name')}
                                                                disabled={isSaving}
                                                            />
                                                        )}

                                                        {/* Show selected item info */}
                                                        {item.useExisting && item.item_id && (
                                                            <div className="text-sm text-gray-600 dark:text-gray-400 bg-blue-50 dark:bg-blue-900/20 p-2 rounded">
                                                                <div><strong>{t('common.variants.selectedItem', 'Selected Item')}:</strong> {item.item_name}</div>
                                                                <div><strong>{t('common.variants.itemId', 'ID')}:</strong> {item.item_id}</div>
                                                            </div>
                                                        )}

                                                        {/* Quantity and Note */}
                                                        <div className="grid grid-cols-2 gap-3">
                                                            <InputField
                                                                label={t('common.variants.quantity', 'Quantity')}
                                                                type="number"
                                                                min="1"
                                                                value={item.quantity}
                                                                onChange={(event: ChangeEvent<HTMLInputElement>) => handleComboItemChange(item.id, 'quantity', parseInt(event.target.value, 10) || 1)}
                                                                disabled={isSaving}
                                                            />
                                                            <InputField
                                                                label={t('common.variants.note', 'Note')}
                                                                value={item.note}
                                                                onChange={(event: ChangeEvent<HTMLInputElement>) => handleComboItemChange(item.id, 'note', event.target.value)}
                                                                placeholder="Optional note"
                                                                disabled={isSaving}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {(error || externalError) && (
                                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300">
                                    {error || externalError}
                                </div>
                            )}
                        </div>

                        <div className="mt-6 flex flex-col gap-3 border-t border-gray-200 pt-4 dark:border-gray-700 md:flex-row md:items-center md:justify-between">
                            <button
                                type="button"
                                onClick={handleReset}
                                className="text-sm font-medium text-amber-600 transition-colors hover:text-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
                                disabled={isSaving}
                            >
                                {t('common.variants.resetToDefault', 'Reset to branch defaults')}
                            </button>
                            <div className="flex flex-col gap-2 md:flex-row">
                                <Button
                                    variant="outline"
                                    onClick={onClose}
                                    disabled={isSaving}
                                >
                                    {t('common.cancel', 'Cancel')}
                                </Button>
                                <Button
                                    onClick={handleSave}
                                    disabled={isSaving}
                                >
                                    {isSaving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
                                </Button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default ItemVariantOverrideModal;
